import db from "../db/database";
import { CashRegister } from "../interfaces/cash-register";
import { markForDelete, markForSync } from "./syncService";
import { getTPVByNoOrId } from "./tpvService";
import { buscarValorEnUso, buscarValorEnUsoAlEditar } from "./unicidad";

const indexDB = db as any;

// Servicio para manejar las operaciones relacionadas con las cajas

// Agregar una nueva caja a la base de datos
export const addCaja = async (caja: any): Promise<CashRegister> => {
  try {
    const nombreEnUso = await buscarValorEnUso(indexDB.cajas, "nombre", caja.nombre);

    if (nombreEnUso) {
      throw new Error(`Ya existe una caja con ese nombre: ${nombreEnUso.nombre}`);
    }

    // El No ahora se puede escribir en el formulario, así que hay que impedir
    // el duplicado: es la clave con la que BC identifica la caja y con la que
    // las aperturas, los cierres y las facturas la referencian.
    if (caja.No) {
      const codigoEnUso = await buscarValorEnUso(indexDB.cajas, "No", caja.No);

      if (codigoEnUso) {
        throw new Error(`Ya existe una caja con el No. ${codigoEnUso.No}`);
      }
    }

    // Si se especificó un TPV, obtener su nombre. Se resuelve por No o por id:
    // las cajas que vienen de BC guardan el No de la tienda (mapCajaFromBC) y
    // el formulario ahora guarda lo mismo, pero pueden quedar cajas viejas con
    // el id local.
    let tpvNombre = null;
    if (caja.TPV) {
      const tpv = await getTPVByNoOrId(caja.TPV);
      tpvNombre = tpv ? tpv.nombre : null;
    }

    // Agregar la caja a la base de datos
    const id = await indexDB.cajas.add({
      nombre: caja.nombre,
      TPV: caja.TPV || null,
      tpvNombre: tpvNombre,
      createdAt: new Date(),
      pendingSync: 1, // Marcar como pendiente de sincronización
      lastModified: new Date(),
      No: caja.No || null, // Guardar el No de Business Central si existe
      deleted: false,
    });

    return { id, ...caja, tpvNombre };
  } catch (error) {
    console.error("Error al agregar caja:", error);
    throw error;
  }
};

// Obtener todas las cajas
export const getAllCajas = async (): Promise<CashRegister[]> => {
  try {
    // Obtener solo cajas no marcadas como eliminadas
    const cajas = await indexDB.cajas.filter((caja: any) => !caja.deleted).toArray();
    return cajas;
  } catch (error) {
    console.error("Error al obtener cajas:", error);
    throw error;
  }
};

// Obtener una caja por ID local
export const getCajaById = async (id: number): Promise<CashRegister> => {
  try {
    const caja = await indexDB.cajas.get(id);
    if (!caja || caja.deleted) {
      throw new Error("Caja no encontrada");
    }
    return caja;
  } catch (error) {
    console.error(`Error al obtener caja con ID ${id}:`, error);
    throw error;
  }
};

// Obtener una caja por No de Business Central
export const getCajaByNo = async (no: string): Promise<CashRegister> => {
  try {
    const caja = await indexDB.cajas
      .where("No")
      .equals(no)
      .and((caja: any) => !caja.deleted)
      .first();

    if (!caja) {
      throw new Error("Caja no encontrada");
    }
    return caja;
  } catch (error) {
    console.error(`Error al obtener caja con No ${no}:`, error);
    throw error;
  }
};

/**
 * La caja, por su clave interna o por su `No`. **Devuelve un objeto, no una
 * lista**, y lanza si no la encuentra.
 *
 * El tipo decía `CashRegister[]` y no lo era. Las ocho llamadas del proyecto la
 * usaban bien --como objeto--, pero quien se fiara de la firma escribiría
 * `[0]`, sacaría `undefined` y creería que la caja no existe. Pasó.
 */
export const getCajaByNoOrId = async (noOrId: string | number): Promise<CashRegister> => {
  try {
    let caja = await indexDB.cajas.get(noOrId);
    if (!caja) {
      caja = await getCajaByNo(noOrId as string);
    }
    return caja;
  } catch (error) {
    console.error(`Error al obtener caja con No o ID ${noOrId}:`, error);
    throw error;
  }
};

// Actualizar una caja existente
export const updateCaja = async (id: number, cajaData: any): Promise<CashRegister> => {
  try {
    const caja = await indexDB.cajas.get(id);
    if (!caja || caja.deleted) {
      throw new Error("Caja no encontrada");
    }

    const nombreEnUso = await buscarValorEnUsoAlEditar(indexDB.cajas, "nombre", cajaData, caja);

    if (nombreEnUso) {
      throw new Error(`Ya existe una caja con ese nombre: ${nombreEnUso.nombre}`);
    }

    const codigoEnUso = await buscarValorEnUsoAlEditar(indexDB.cajas, "No", cajaData, caja);

    if (codigoEnUso) {
      throw new Error(`Ya existe una caja con el No. ${codigoEnUso.No}`);
    }

    // Si se actualizó el TPV, obtener su nombre (por No o por id, ver addCaja)
    let tpvNombre = caja.tpvNombre;
    if (cajaData.TPV !== undefined && cajaData.TPV !== caja.TPV) {
      if (cajaData.TPV) {
        const tpv = await getTPVByNoOrId(cajaData.TPV);
        tpvNombre = tpv ? tpv.nombre : null;
      } else {
        tpvNombre = null;
      }
    }

    // Actualizar la caja
    await indexDB.cajas.update(id, {
      ...cajaData,
      tpvNombre: tpvNombre,
      lastModified: new Date(),
    });

    // Marcar como pendiente de sincronización
    await markForSync("cajas", id);

    return await getCajaById(id);
  } catch (error) {
    console.error(`Error al actualizar caja con ID ${id}:`, error);
    throw error;
  }
};

// Eliminar una caja
export const deleteCaja = async (id: number): Promise<boolean> => {
  try {
    const caja = await indexDB.cajas.get(id);
    if (!caja) {
      throw new Error("Caja no encontrada");
    }

    // Marcar como eliminada y pendiente de sincronización
    await markForDelete("cajas", id);

    return true;
  } catch (error) {
    console.error(`Error al eliminar caja con ID ${id}:`, error);
    throw error;
  }
};
